import { CardHeader, Label, Spinner } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getApiFolderPermissionsByGraphFolderIdOptions } from '../../api/@tanstack/react-query.gen';
import { FolderPermissionsResponse } from '../../api/types.gen';
import { ErrorMessageBar } from '../Core/ErrorMessageBar';
import { LoadingSkeleton } from '../Core/LoadingSkeleton';
import FolderPermissionInfoTable from './components/FolderPermissionInfoTable';
import { PermissionsDialogForm } from './components/modifyPermissionsDialog/PermissionsDialogForm';
import { useFolderPermissionsViewOperations } from './hooks/useFolderPermissionsViewOperations';

export default function FolderPermissionsView({
  graphFolderId,
  exchangeFolderId,
  folderName,
}: Readonly<{
  graphFolderId: string;
  // Required: every permission endpoint needs it to address the folder in Exchange.
  exchangeFolderId: string;
  folderName?: string;
}>) {
  const { t } = useTranslation();

  const {
    data: folderPermissions,
    error: queryError,
    isLoading: isQueryLoading,
    isFetching,
    refetch,
  } = useQuery({
    ...getApiFolderPermissionsByGraphFolderIdOptions({
      path: {
        graphFolderId,
      },
      query: {
        exchangeFolderId,
      },
    }),
  });

  const {
    handleAdd,
    handleEdit,
    handleDelete,
    isLoading: isBusy,
  } = useFolderPermissionsViewOperations({ graphFolderId, exchangeFolderId, refetch });

  if (isQueryLoading) {
    return <LoadingSkeleton />;
  }

  if (queryError) {
    return <ErrorMessageBar error={queryError} messageBarTitle={t('permissions.errors.couldNotLoadError')} />;
  }

  return (
    <>
      <CardHeader
        header={
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Label size="large" weight="semibold">
              {folderName}
            </Label>
            {(isBusy || isFetching) && <Spinner size="tiny" />}
          </div>
        }
      />
      <div>
        <span style={{ width: '12px' }} />
        <PermissionsDialogForm
          onSave={handleAdd}
          isLoading={isBusy}
          dialogTitle={t('permissions.modifyAction.addTitle')}
          openingButtonLabel={t('permissions.modifyAction.addTitle')}
          folderInfo={folderPermissions ?? ({} as FolderPermissionsResponse)}
        />
      </div>
      <FolderPermissionInfoTable
        folderInfo={folderPermissions ?? ({} as FolderPermissionsResponse)}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
        isLoading={isBusy}
      />
    </>
  );
}
